import { useEffect, useState } from 'react';
import { useApi } from 'hooks';
import GroupConfigurationAPI from 'services/API/GroupConfigurationAPI';
import { useUser } from 'state/auth';
import { NEW_GROUP_ID } from 'utils/constants';
import type { GroupRawResponse } from 'types';

const fetchGroupData = async (
  groupId: string | undefined,
  api: any,
): Promise<GroupRawResponse | null> => {
  if (!groupId || groupId === NEW_GROUP_ID) {
    return null;
  }
  const response = await api.get(GroupConfigurationAPI.getProjectGroup(groupId));

  if (response?.data) {
    return response.data;
  }

  return null;
};

export const useGroupData = (
  groupId: string | undefined,
  // Optional extra value to force a refetch of the same groupId (e.g. a
  // saved search's query_md5, once a chat turn reports its filters changed).
  cacheBuster?: string,
): GroupRawResponse | null => {
  const [group, setGroup] = useState<GroupRawResponse | null>(null);
  const api = useApi();
  const { token } = useUser();

  useEffect(() => {
    // Guard against out-of-order responses: if groupId/cacheBuster changes
    // again before this fetch resolves, its (now stale) result must not
    // overwrite the state for the newer request - e.g. switching from one
    // saved search to another shouldn't let the first one's slower response
    // land last and clobber the second one's filters.
    let isCurrent = true;
    fetchGroupData(groupId, api).then((data) => {
      if (isCurrent) {
        setGroup(data);
      }
    });

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, token, cacheBuster]);

  return group;
};
